import React, {CSSProperties, ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import classNames from "classnames";
import {HotkeysEvent, KeyHandler} from "hotkeys-js";


import {HotKeyOptions, hotkeys, HOTKEYS_SCOPE, HotkeysScopeValue, setScope} from "../../../utils/hotkeys";
import {screenSizeProvider} from "../../../stores/ScreenSizeProvider";


export type OnItemClick = (option: ContextMenuWithNavigationItem) => boolean | undefined | void;

export interface ContextMenuItemRenderMeta {
    isInNavigationFocus: boolean;
    onClick: () => void;
}

export interface ContextMenuWithNavigationItem {
    id: string;
    getContent: (item: ContextMenuWithNavigationItem, meta: ContextMenuItemRenderMeta, innerRef: any) => React.ReactNode;
    preserveContextMenu?: boolean;
    condition?: boolean;
    text?: ReactNode;
    textForFilter?: string;
    meta?: any;
}

export interface ContextMenuWithNavigationProps {
    handleClose: () => void;
    itemGroups: ContextMenuWithNavigationItem[][];
    onItemClick: OnItemClick;
    onNewOption?: () => void;
    filterQuery?: string;
    onEmptyEnter?: () => void;
    allowFilter?: boolean;
    addNewRowNode?: React.ReactNode;
    headerNode?: React.ReactNode;
    notFoundNode?: React.ReactNode;
    maxWidth?: string;
    maxHeight?: string;
    className?: string;
    isDark?: boolean;
    withBorder?: boolean;
}


interface MenuState {
    filteredItems: (ContextMenuWithNavigationItem | 'divider')[];
    navigationFocusedItem: ContextMenuWithNavigationItem | null;
}

const bindHotkey = (key: string, action: KeyHandler, options?: HotKeyOptions) => {
    hotkeys(
        key,
        {
            scope: HOTKEYS_SCOPE.CONTEXT_MENU,
            keyup: false,
            keydown: true,
            ...options,
        },
        action
    )
}

export const ContextMenuWithNavigation = (
    {
        itemGroups,
        handleClose,
        filterQuery,
        onNewOption,
        onEmptyEnter,
        allowFilter,
        onItemClick,
        addNewRowNode,
        headerNode,
        notFoundNode,
        maxWidth,
        maxHeight,
        isDark,
        className,
        withBorder,
    }: ContextMenuWithNavigationProps
) => {
    const [menuItemsState, setMenuItemsState] = useState<MenuState>({
        filteredItems: [],
        navigationFocusedItem: null,
    });
    const [hideNavigationHover, setHideNavigationHover] = useState(false);
    const isMobile = screenSizeProvider.view !== 'desktop';

    const refMap = useRef(new WeakMap<ContextMenuWithNavigationItem, HTMLDivElement | null>());
    const prevHotkeysScope = useRef<HotkeysScopeValue | null>(null);

    const refNavigationFocusedItem = useRef<ContextMenuWithNavigationItem | null>(null);
    const refFiltered = useRef<(ContextMenuWithNavigationItem | 'divider')[]>([]);

    refNavigationFocusedItem.current = menuItemsState.navigationFocusedItem;
    refFiltered.current = menuItemsState.filteredItems;

    const handleItemClick = (item: ContextMenuWithNavigationItem) => {
        const clickResult = onItemClick(item);
        if (!item.preserveContextMenu && clickResult !== false) {
            handleClose();
        }
    }

    useLayoutEffect(() => {
        const _filteredActions: (ContextMenuWithNavigationItem | 'divider')[] = [];
        const emptyQuery = (filterQuery === undefined || filterQuery === '');
        const filterText = filterQuery?.toLowerCase();
        for (const itemGroup of itemGroups) {
            let isGroupEmpty = true;
            for (const ctxMenuItem of itemGroup) {
                if (ctxMenuItem.condition !== undefined && !ctxMenuItem.condition) {
                    continue;
                }

                if (!allowFilter || emptyQuery) {
                    _filteredActions.push(ctxMenuItem);
                    isGroupEmpty = false;
                } else {
                    const startAt = ctxMenuItem.textForFilter!.toLowerCase().indexOf(filterText!);
                    if (startAt !== -1) {
                        const endAt = startAt + filterText!.length;
                        _filteredActions.push({
                            ...ctxMenuItem,
                            text: ctxMenuItem.textForFilter!.substring(0, startAt) + `<em>${ctxMenuItem.textForFilter!.substring(startAt, endAt)}</em>` + ctxMenuItem.textForFilter!.substring(endAt),
                        });
                        isGroupEmpty = false;
                    }
                }
            }
            if (!isGroupEmpty) {
                _filteredActions.push('divider');
            }
        }
        _filteredActions.pop(); /* remove trailing group divider */
        if (!!onNewOption && !emptyQuery) {
            _filteredActions.push({
                id: 'add_new',
                getContent: () => addNewRowNode,
            });
        }
        setMenuItemsState({
            filteredItems: _filteredActions,
            navigationFocusedItem: (_filteredActions[0] as ContextMenuWithNavigationItem),
        });

    }, [filterQuery, itemGroups]);

    const moveTooltipSelection = useCallback((direction: 'up' | 'down') => {
        return (keyboardEvent: KeyboardEvent, hotkeysEvent: HotkeysEvent) => {
            setHideNavigationHover(false);
            setMenuItemsState((menuItemsPrevState) => {
                return updateMenuState(menuItemsPrevState, direction, refMap.current);
            });
            keyboardEvent.preventDefault();
            keyboardEvent.stopPropagation();
        }
    }, [setHideNavigationHover, setMenuItemsState]);

    useEffect(() => {
        prevHotkeysScope.current = hotkeys.getScope() as HotkeysScopeValue;
        setScope(HOTKEYS_SCOPE.CONTEXT_MENU);
        bindHotkey("up", moveTooltipSelection('up'));
        bindHotkey("down", moveTooltipSelection('down'));
        bindHotkey('enter,num_enter', (keyboardEvent, hotkeysEvent) => {
            if (refFiltered.current.length === 0) {
                onEmptyEnter && onEmptyEnter();
            }
            keyboardEvent.stopPropagation();
            keyboardEvent.preventDefault();
            if (refNavigationFocusedItem.current) {
                handleItemClick(refNavigationFocusedItem.current);
            }
        }, {
            keyup: true,
            keydown: false,
        });

        return () => {
            hotkeys.unbind("up", HOTKEYS_SCOPE.CONTEXT_MENU);
            hotkeys.unbind("down", HOTKEYS_SCOPE.CONTEXT_MENU);
            hotkeys.unbind("enter,num_enter", HOTKEYS_SCOPE.CONTEXT_MENU);
            hotkeys.unbind("Escape", HOTKEYS_SCOPE.CONTEXT_MENU);

            setScope(prevHotkeysScope.current!);
        }
    }, []);

    let divider_id = 0;

    const style: CSSProperties = {};
    if (maxWidth && !isMobile) {
        style.maxWidth = maxWidth;
    }

    if (maxHeight) {
        style.maxHeight = maxHeight;
    }

    return (
        <div className={classNames({
            [className!]: !!className,
            'theme-dark': isDark,
            'Select-withBorder': withBorder,
            'ContextMenuWithNavigation--hideNavigationHover': hideNavigationHover,
            'ContextMenuWithNavigation--showNavigationHover': !hideNavigationHover,
        })}
             style={style}
             onMouseMove={() => {
                 if (!hideNavigationHover) {
                     setHideNavigationHover(true);
                 }
             }}
        >
            {
                headerNode ? headerNode : null
            }
            {
                menuItemsState.filteredItems.length
                    ? (
                        menuItemsState.filteredItems.map(filteredItem => {
                            if (!isContextMenuWithNavigationItem(filteredItem)) {
                                return (
                                    <div key={`divider_${divider_id++}`} className='ContextMenuInEditor__divider'/>
                                )
                            } else {
                                return filteredItem.getContent(
                                    filteredItem,
                                    {
                                        isInNavigationFocus: filteredItem === menuItemsState.navigationFocusedItem,
                                        onClick: () => handleItemClick(filteredItem),
                                    },
                                    (dom: HTMLDivElement) => refMap.current.set(filteredItem, dom)
                                );
                            }
                        })
                    )
                    : (
                        notFoundNode
                    )
            }
        </div>
    )
}

function isContextMenuWithNavigationItem(obj: 'divider' | ContextMenuWithNavigationItem): obj is ContextMenuWithNavigationItem {
    return typeof obj !== 'string';
}

function updateMenuState(menuItemsPrevState: MenuState, direction: 'up' | 'down', domRef: WeakMap<ContextMenuWithNavigationItem, HTMLDivElement | null>) {
    let inFocusUpdate = menuItemsPrevState.navigationFocusedItem;
    const filteredItems = menuItemsPrevState.filteredItems;
    if (menuItemsPrevState.navigationFocusedItem === null) {
        inFocusUpdate = direction === 'down'
            ? filteredItems[0] as ContextMenuWithNavigationItem
            : filteredItems[filteredItems.length - 1] as ContextMenuWithNavigationItem;
    } else {
        const currentIndex = filteredItems.indexOf(menuItemsPrevState.navigationFocusedItem);
        if (currentIndex === 0 && direction === 'up') {
            inFocusUpdate = filteredItems[filteredItems.length - 1] as ContextMenuWithNavigationItem;
        } else {
            const nextIndex = direction === 'down'
                ? (currentIndex + 1) % filteredItems.length
                : (currentIndex - 1) % filteredItems.length;
            const nextTarget = filteredItems[nextIndex];
            if (isContextMenuWithNavigationItem(nextTarget)) {
                inFocusUpdate = nextTarget;
            } else {
                inFocusUpdate = filteredItems[direction === 'down' ? nextIndex + 1 : nextIndex - 1] as ContextMenuWithNavigationItem;
            }
        }
    }
    const domNode = domRef.get(inFocusUpdate);
    if (domNode) {
        domNode.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
        });
    }
    return {
        filteredItems: menuItemsPrevState.filteredItems,
        navigationFocusedItem: inFocusUpdate,
    };
}
